<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :padding="false"
        size="md">
        <template v-slot:header>
            <div class="p-6">
                Media
            </div>
        </template>

        <div class="p-12 pt-10 flex flex-col gap-4">
            <vx-alert :closeable="false">
                <span v-if="props.blockImages && !props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: mp4, mp3, csv, pdf, vcard</span>
                <span v-else-if="!props.blockImages && props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: gif, jpg, jpeg, png, heic</span>
                <span v-else>Allowed media types:<br/> gif, jpg, jpeg, png, heic, csv, pdf, vcard, mp4, mp3</span>
            </vx-alert>
            <input type="file"
                id="media-input"
                class="absolute opacity-0"
                @change="fileChange">
            <label v-if="!url"
                data-test="media-drop"
                for="media-input"
                class="px-8 py-16 bg-slate-50 rounded-2xl border-dashed border-2 border-slate-300 flex-col justify-center items-center gap-2 flex cursor-pointer mb-0"
                :class="{
                    'border-sky-200 bg-white': dragging,
                }"
                @dragenter.prevent.stop="dragging = true"
                @dragover.prevent.stop="dragging = true"
                @dragleave.prevent.stop="dragging = false"
                @drop.prevent.stop="dropped">
                <font-awesome-icon widthAuto v-if="!uploading"
                    :icon="faPhotoFilmMusic"
                    class="text-sky-400 text-5xl">
                </font-awesome-icon>
                <vx-icon-loading v-else
                    class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                <div class="text-center text-balance text-slate-700 text-sm font-normal">
                    <template v-if="uploading">
                        Uploading...
                    </template>
                    <template v-else>
                        Drag media here or<br/> click to open file browser.
                    </template>
                </div>
            </label>
            <div v-if="url"
                class="w-96 max-w-full mx-auto">
                <message-builder-media-preview :mediaUrl="url"
                    :mediaContentType="contentType"
                    @remove="remove()"
                    removeable>
                </message-builder-media-preview>
            </div>
            <div class="text-center text-slate-500 text-sm">
                Limited to one attachment with 1MB file size.
                <template v-if="url">
                    <br />
                    Remove existing file to add a new one.
                </template>
            </div>
            <vx-alert v-if="error"
                :closeable="false"
                color="danger"
                class="whitespace-pre-line">{{ error }}</vx-alert>
            <vx-alert v-if="warning"
                color="info"
                :closeable="false">
                {{ warning }}
            </vx-alert>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click.prevent="emit('update:visible', false)"
                    color="muted"
                    size="lg"
                    type="button">
                    Cancel
                </vx-button>
                <vx-button @click.prevent="save()"
                    type="button"
                    color="primary"
                    size="lg"
                    class="grow"
                    data-test="save-btn"
                    :disabled="!url"
                    :loading="uploading">
                    Save
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxButton, VxModal, VxAlert, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, ref, watch } from 'vue';
import { messageMediaClient } from '~/services';
import { faCircleCheck, faPhotoFilmMusic } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import MessageBuilderMediaPreview from './MessageBuilderMediaPreview.vue';
import ImageBlobReduce from 'image-blob-reduce';
import Heic2Any from 'heic2any'
import { ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPES } from '~/utils/message';

const props = defineProps({
    visible: Boolean,
    mediaUrl: {
        type: String,
        required: false,
    },
    mediaContentType: {
        type: String,
        required: false,
    },
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'update:mediaUrl', 'update:mediaContentType']);

const dragging = ref(false);
const uploading = ref(false);
const teamId = Spark.state.currentTeam.id;

const error = ref('');
const warning = ref('');
const url = ref(undefined);
const contentType = ref(undefined);

const validateFileType = (file) => {
    if (
        (props.blockImages && !props.blockFiles && !ALLOWED_FILE_TYPES.includes(file.type)) ||
        (!props.blockImages && props.blockFiles && !ALLOWED_IMAGE_TYPES.includes(file.type))
    ) {
        error.value = 'The file type is not supported.';

        return false;
    }

    return true;
}

const dropped = (event) => {
    if (event.dataTransfer.files[0]) {
        if (validateFileType(event.dataTransfer.files[0])) {
            upload(event.dataTransfer.files[0])
        }
    }
    dragging.value = false;
}

const fileChange = (event) => {
    if (event.target.files[0]) {
        if (validateFileType(event.target.files[0])) {
            upload(event.target.files[0]);
        }
        event.target.value = '';
    }
}

const imageDimensions = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                resolve([img.width, img.height]);
            }
            img.onerror = (error) => {
                reject(error)
            }
            img.src = event.target.result;
        }
        reader.onerror = (error) => {
            reject(error)
        }
        reader.readAsDataURL(file);
    })
}

const halveImageDimension = async (file) => {
    const [width] = await imageDimensions(file);

    const resizedWidth = width / 2;
    const reducer = new ImageBlobReduce()
    const result = await reducer.toBlob(file, { max: resizedWidth })

    const resizedFile = new File([result], file.name, {
        type: file.type,
    });

    return resizedFile;
}

const reduceImageQuality = async (file, quality) => {
    if (quality && (quality > 1 || quality < 0.1)) {
        throw new Error('reduceImageQuality(): quality should be between 0.1-1.0. Provided: ' + quality)
    }
    const reducer = new ImageBlobReduce()

    reducer._create_blob = function (env) {
        return this.pica.toBlob(env.out_canvas, file.type, quality)
            .then(function (blob) {
                env.out_blob = blob;
                return env;
            });
    };
    const result = await reducer.toBlob(file)

    return new File([result], file.name, {
        type: file.type,
    });
}

const inTargetSizeRange = (size) => {
    return ((size / 1000) < 600) && ((size / 1000) > 500);
}
/**
 * @param {File} file
 */
const convertHeicToJpeg = async (file) => {
    const blob = await Heic2Any({
        blob: file,
        toType: 'image/jpeg',
    });

    // if heic ended up being bigger than 600KB resize it
    if (blob.size / 1000 > 600) {
        return resizeImage(blob);
    }
    return blob;
}

/**
 * Resize image to find the best quality option between 500KB and 600KB
 * @param {File} originalFile
 */
const resizeImage = async (originalFile) => {
    let processed = 0;

    // files that were processed and are smaller than 600KB
    const attempts = [];

    const processQuality = async (file, attempt = 1, lowQuality = 0.6, highQuality = 1) => {
        if (processed > 10) {
            // avoids iterating more than 10 times
            return;
        }
        processed++;
        // the first attempt we try to reduce it to 0.6, then on each attempt
        // we reduce the quality until we find the best image size
        const quality = attempt === 1 ? 0.6 : (lowQuality + highQuality) / 2;
        const reducedQuality = await reduceImageQuality(file, quality);

        // when its the first attempt and the image size is bigger
        // than 600KB, we stop reducing the quality and let
        // the processImage halve the image dimension
        if (attempt === 1 && (reducedQuality.size / 1000) > 600) {
            return;
        }

        if (inTargetSizeRange(reducedQuality.size)) {
            // found correct size quality, aborts the process
            attempts.push(reducedQuality);
            return;
        }

        if (reducedQuality.size / 1000 < 500) {
            if (highQuality - lowQuality > 0.05) {
                // Too small, increase quality
                await processQuality(file, attempt + 1, quality, highQuality);
            } else {
                attempts.push(reducedQuality); // Close enough
            }
        } else {
            // Too large, decrease quality
            await processQuality(file, attempt + 1, lowQuality, quality);
        }
    }

    const processImage = async (file) => {
        if (processed > 10) {
            // avoids iterating more than 10 times
            return;
        }

        await processQuality(file);

        if (attempts.length > 0) {
            return;
        }

        // when it wasn't possible to keep the dimension and reduce the quality
        // we halve the image dimension and process it again
        const halvedSize = await halveImageDimension(file);
        await processImage(halvedSize);
    }

    await processImage(originalFile);

    if (!attempts.length) {
        throw new Error('Could not resize the image to be smaller than 600KB.')
    }

    // picks the attempt that has the highest size
    return attempts.sort((a, b) => b.size - a.size)[0];
}

const upload = async (file) => {
    if (uploading.value) {
        return;
    }
    try {
        uploading.value = true;
        await processUpload(file)
    } finally {
        uploading.value = false;
    }
}

/**
 * @param {File} file
 */
const processUpload = async (file) => {
    const staticImage = ['image/jpeg', 'image/png'].includes(file.type);
    const heicImage = 'image/heic' === file.type;
    const biggerThan600KB = file.size / 1000 > 600;
    const smallerThan1MB = file.size / 1000 / 1000 <= 1;

    if (!smallerThan1MB && !staticImage && !heicImage) {
        error.value = 'Max file size is 1MB.';
        return;
    }

    if (file.size / 1000 / 1000 > 20) {
        error.value = 'The source image must not be more than 20MB.'
        return;
    }

    try {
        error.value = '';

        const formData = new FormData();
        let uploadedContentType = file.type;

        if (heicImage) {
            const blob = await convertHeicToJpeg(file);
            uploadedContentType = blob.type || 'image/jpeg';
            // removes .heic extension and add .jpeg
            const fileName = `${file.name.slice(0, -5)}.jpeg`;
            formData.append('file', blob, fileName)

            warning.value = 'The image has been automatically converted from HEIC format to JPEG and resized to be under 600KB.';
        } else if (biggerThan600KB && staticImage) {
            const blob = await resizeImage(file);
            formData.append('file', blob, file.name)

            warning.value = 'The image has been automatically resized to be under 600KB.';
        } else {
            formData.append('file', file);
        }

        const response = await messageMediaClient.uploadMedia(teamId, formData)
        url.value = response.data.url;
        contentType.value = uploadedContentType;

        if (biggerThan600KB && !staticImage && !heicImage) {
            warning.value = 'The attachment exceeds 600KB. Please note that it may fail to be delivered, and you may still be charged. We recommend replacing the attachment with something smaller than 600KB in size to ensure successful delivery and avoid potential charges.';
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
            error.value = e.response.data?.errors?.file?.[0] || 'The file failed to upload.';
        } else if (e?.message?.toLowerCase?.()?.includes('fingerprinting protection')) {
            error.value = 'It was not possible to resize the image due to fingerprinting protection. Upload an image smaller than 600KB.';
        } else {
            error.value = 'The file failed to upload. Max file size is 1MB.';
        }
    }
}

const remove = () => {
    error.value = '';
    warning.value = '';
    url.value = undefined;
    contentType.value = undefined;
}

const save = async () => {
    if (uploading.value) {
        return;
    }

    emit('update:mediaContentType', contentType.value);
    await nextTick();
    emit('update:mediaUrl', url.value);
    emit('update:visible', false)
}

watch(() => props.visible, () => {
    uploading.value = false;
    dragging.value = false;
    error.value = '';
    warning.value = '';
    url.value = props.mediaUrl;
    contentType.value = props.mediaContentType;
});
</script>
